/*
 * Industry content — `en` (international market, source locale).
 *
 * This is the international variant of every industry (the old per-field `*Intl`
 * copy, resolved): generic time-registration framing, no belgium-specific pains/
 * features, and `compliance.slug` / `relatedArticles` pointing only at articles
 * that exist under `/en/` (the 4 market-agnostic posts). `nl.ts` carries the
 * belgium variant. Resolved by `industriesFor("en")` (also the SOURCE_LOCALE
 * fallback). Pure data (NFR10).
 */
import type { Industry } from "./types";

export const INDUSTRIES_PT: Industry[] = [
  {
    slug: "construction",
    name: "Construção",
    headline: "Registo de tempo para equipas de construção — check-in com verificação de localização, horas por obra e controlo de orçamento, numa só aplicação",
    intro: "Equipas espalhadas por várias obras, horas anotadas em papel, horas de subcontratados impossíveis de reconciliar. A Done-it dá a cada obra uma folha de horas digital à prova de água que alimenta o seu processamento salarial com um clique.",
    pains: [
      {
        title: "Sem registo fiável de quem trabalhou onde",
        body: "Com várias obras ativas e equipas que rodam, as folhas de horas manuais estão erradas por natureza. Os trabalhadores arredondam, esquecem-se de registos ou preenchem-nos dias depois — deixando-o com números salariais que não consegue defender.",
      },
      {
        title: "Os desvios de orçamento aparecem demasiado tarde",
        body: "Quando percebe que uma obra já consumiu as horas planeadas, a margem já se foi. Não há uma contagem decrescente em tempo real — apenas uma surpresa desagradável na altura de faturar.",
      },
      {
        title: "A preparação do salário consome horas todos os meses",
        body: "Recolher as folhas de horas, reconciliá-las com os projetos e preparar a exportação para o processamento salarial demora dias. Cada registo é um erro em potencial.",
      },
    ],
    features: [
      {
        title: "Check-in com verificação de localização por obra",
        body: "Um toque regista a entrada do trabalhador, com verificação de localização, e associa as horas ao projeto certo. Sem leitores de cartões, sem terminais — apenas o smartphone que a sua equipa já tem consigo.",
      },
      {
        title: "Horas restantes em tempo real por projeto",
        body: "Defina as horas planeadas como orçamento quando abre o projeto. Os trabalhadores veem uma contagem decrescente do que resta; o gestor vê-a em tempo real no painel. Os desvios de orçamento são visíveis antes de acontecerem.",
      },
      {
        title: "Gestão de subcontratados e presença",
        body: "Adicione subcontratados a um projeto, atribua-lhes o check-in e acompanhe as suas horas a par da sua própria equipa — na mesma visão geral.",
      },
      {
        title: "Exportação salarial com um clique",
        body: "Todas as horas registadas — por trabalhador, por projeto — exportam para uma folha de cálculo limpa para o seu prestador de processamento salarial. Sem preparação manual, sem erros.",
      },
    ],
    compliance: {
      label: "Registo de tempo digital",
      title: "Check-in com verificação de localização concebido para obras de construção",
      body: "A Done-it oferece check-in com verificação de localização, registo de tempo por projeto e uma exportação salarial limpa para equipas de construção. Para equipas que operam em obras belgas, alimenta também automaticamente o check-in no trabalho.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "A equipa chega à obra", body: "Cada trabalhador toca uma vez para fazer check-in. O GPS confirma a localização, regista a hora da entrada e associa as horas ao projeto. Sem papel, sem terminal." },
      { step: "Vê tudo em tempo real", body: "Horas, orçamento restante, presença de subcontratados e qualquer trabalho extra reportado da obra — tudo no seu painel, em tempo real." },
      { step: "Fecho do mês em minutos", body: "Exporte todas as horas por trabalhador e por projeto. Envie ao seu prestador de processamento salarial. A Done-it gera relatórios limpos e prontos para auditoria automaticamente." },
    ],
    stats: [
      { value: "1 toque", label: "para fazer check-in em qualquer obra — sem terminal, sem papel" },
      { value: "1 clique", label: "exportação salarial pronta para o seu prestador" },
      { value: "Tempo real", label: "vista de horas restantes por projeto e obra" },
    ],
    quote: {
      text: "A nossa equipa faz o seu próprio check-in na obra. No fecho do mês, o salário simplesmente fecha — deixámos por completo de andar atrás das folhas de horas.",
      who: "Proprietário, empresa de construção · 14 colaboradores",
      result: "5 horas por semana poupadas em tarefas administrativas",
    },
    relatedSlugs: ["installation", "maintenance", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "cleaning",
    name: "Limpeza",
    headline: "Registo de tempo para empresas de limpeza — check-in com verificação de localização, prova de presença e salário numa só aplicação",
    intro: "Os funcionários de limpeza deslocam-se entre locais durante todo o dia. A Done-it acompanha quem está onde em tempo real — check-in com verificação de localização em cada local, dedução automática de pausas e uma exportação salarial limpa no fecho do mês.",
    pains: [
      {
        title: "Provar a presença em cada local",
        body: "Clientes e inspetores esperam registos de presença à prova de água. As folhas manuais podem ser contestadas. Cada registo é verificado por localização.",
      },
      {
        title: "Equipas espalhadas por muitos locais",
        body: "Com os funcionários de limpeza a deslocarem-se entre locais ao longo do dia, não tem uma visão em tempo real de quem está a trabalhar onde — até alguém ligar.",
      },
      {
        title: "Erros salariais por horas arredondadas",
        body: "Sem horas exatas de entrada e saída, o salário é reconstruído de memória. As horas são arredondadas para cima, os custos inflacionam e seguem-se as disputas.",
      },
    ],
    features: [
      {
        title: "Check-in e check-out em tempo real",
        body: "Os trabalhadores registam a entrada e a saída no telemóvel em cada local, com verificação de localização; o tempo é exato, com registo de pausas.",
        plan: "Time Basic",
      },
      {
        title: "Dedução automática de pausas",
        body: "As pausas obrigatórias são subtraídas do dia de trabalho automaticamente — sem cálculos manuais, sem disputas.",
        plan: "Time Basic",
      },
      {
        title: "Visão geral em tempo real de vários locais",
        body: "Veja que funcionários de limpeza estão em que locais neste momento. Detete falhas antes dos clientes.",
      },
      {
        title: "Registo de ausências com justificações de baixa digitais",
        body: "Os trabalhadores carregam certificados de baixa e documentos de ausência diretamente a partir da aplicação. Crie os tipos de ausência que a sua equipa realmente utiliza.",
      },
      {
        title: "Exportação salarial com um clique",
        body: "As horas exatas fluem diretamente para uma exportação pronta para o processamento salarial do seu prestador. Sem transcrição, sem erros.",
      },
    ],
    compliance: {
      label: "Presença com verificação de localização",
      title: "Presença em tempo real em todos os locais de limpeza",
      body: "A Done-it dá às equipas de limpeza check-in com verificação de localização em cada local, dedução automática de pausas e uma visão em tempo real de quem está em que local neste momento.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "O funcionário de limpeza chega ao local", body: "Um toque regista a entrada. O GPS regista o local e a hora da entrada. O registo de pausas está integrado na aplicação." },
      { step: "Vê cada local em tempo real", body: "Quem está em que local neste momento — sem telefonemas, sem adivinhação. Detete falhas de cobertura antes dos clientes." },
      { step: "O salário é um clique", body: "Todos os dados de presença — horas exatas, locais e pausas — exportam de forma limpa para o seu prestador de processamento salarial." },
    ],
    stats: [
      { value: "1 toque", label: "para fazer check-in em qualquer local — sem terminal" },
      { value: "Tempo real", label: "vista de quem está em que local neste momento" },
      { value: "0", label: "registos duplicados — as horas fluem diretamente para o salário" },
    ],
    quote: {
      text: "Conseguimos finalmente provar quem limpou onde e quando. Acabaram-se as folhas de presença, acabaram-se as disputas com os clientes — o GPS trata disso.",
      who: "Gestor, empresa de limpeza · 9 colaboradores",
      result: "Zero arredondamentos de horas",
    },
    relatedSlugs: ["maintenance", "field-service", "construction"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "installation",
    name: "Instalação",
    headline: "Registo de tempo para equipas de instalação — horas por projeto, navegação e controlo de orçamento numa só aplicação",
    intro: "Envie os seus instaladores para a morada certa com tudo o que precisam, associe automaticamente cada hora trabalhada ao serviço certo e veja o orçamento restante antes de a obra fechar. A Done-it é a forma mais simples de manter os projetos de instalação dentro do prazo e do custo.",
    pains: [
      {
        title: "A informação do serviço não chega ao terreno a tempo",
        body: "As equipas chegam a uma obra sem a morada, a pessoa de contacto ou os detalhes do âmbito. Esse primeiro telefonema são dez minutos de tempo faturável perdidos antes de se trabalhar uma única hora.",
      },
      {
        title: "As horas não estão associadas ao projeto certo",
        body: "Quando os trabalhadores preenchem as horas manualmente ao fim do dia — se é que as preenchem — as horas vão parar ao serviço errado, ou desaparecem por completo. As faturas não correspondem à realidade.",
      },
      {
        title: "Desvios de orçamento detetados demasiado tarde",
        body: "Sem uma contagem decrescente em tempo real das horas restantes por projeto, só descobre que um serviço excedeu o previsto quando fecha a fatura — não enquanto ainda pode reagir.",
      },
      {
        title: "Horas de subcontratados difíceis de acompanhar",
        body: "Os subcontratados que trabalham na mesma obra usam sistemas diferentes, preenchem as horas em separado ou não o fazem de todo. Reconciliar no fecho do mês é um processo lento e propenso a erros.",
      },
    ],
    features: [
      {
        title: "Informação do projeto partilhada diretamente para a aplicação",
        body: "Os trabalhadores abrem o projeto no telemóvel: morada, horas planeadas, pessoa de contacto e ficheiros carregados (plantas, instruções, PDFs). Tudo o que precisam está lá antes de chegarem.",
        plan: "Project Basic",
      },
      {
        title: "Navegação para a obra com um botão",
        body: "Toque uma vez e o telemóvel navega até à morada do projeto — integrado na aplicação, sem necessidade de uma aplicação de mapas separada.",
      },
      {
        title: "Contagem decrescente de horas restantes por projeto",
        body: "Defina as horas planeadas quando abre o projeto. Os trabalhadores veem quantas horas restam; o gestor vê o estado em tempo real no painel. Os desvios são visíveis antes de acontecerem.",
        plan: "Project Basic",
      },
      {
        title: "Check-in do trabalhador por projeto",
        body: "Cada trabalhador faz check-in no projeto certo. Sabe sempre quem está em que obra neste momento.",
        plan: "Project Basic",
      },
      {
        title: "Registo de trabalho extra e materiais",
        body: "Quando o âmbito aumenta na obra, os trabalhadores registam o trabalho extra e os materiais em tempo real — com texto e fotos — para que nada seja faturado de memória.",
        plan: "Project Basic",
      },
    ],
    compliance: {
      label: "Registo de tempo digital",
      title: "Check-in com verificação de localização por projeto para equipas de instalação",
      body: "A Done-it dá às equipas de instalação check-in com verificação de localização por projeto, uma contagem decrescente de horas restantes em tempo real e exportação salarial automática — cobrindo qualquer requisito local de registo de tempo. Para equipas que trabalham em obras de construção belgas, integra também com o CIAO@work.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "O trabalhador abre o projeto no telemóvel", body: "A morada, o contacto, o âmbito e os ficheiros carregados estão ali mesmo. Navega com um toque — sem chamadas ao escritório a pedir direções." },
      { step: "As horas registam-se automaticamente no serviço certo", body: "O check-in associa cada minuto trabalhado ao projeto correto. O orçamento restante atualiza-se em tempo real." },
      { step: "O trabalho extra é capturado na obra", body: "As alterações de âmbito, os materiais extra e os atrasos são registados com texto e fotos no momento em que acontecem — prontos para a fatura." },
    ],
    stats: [
      { value: "Tempo real", label: "vista de horas restantes por projeto" },
      { value: "0", label: "chamadas ao escritório a pedir moradas de obras" },
      { value: "1 clique", label: "navegue para qualquer morada de projeto a partir da aplicação" },
    ],
    quote: {
      text: "As horas vão parar ao projeto certo automaticamente. O cálculo de custos por serviço passou a ser fácil — conseguimos ver a margem real antes de fechar um serviço.",
      who: "Proprietário, empresa de instalação · 6 colaboradores",
      result: "Custo real por serviço, em tempo real",
    },
    relatedSlugs: ["construction", "electricians", "hvac"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "field-service",
    name: "Assistência técnica no terreno",
    headline: "Registo de tempo para equipas de assistência técnica no terreno — quilometragem, despesas e horas a partir da estrada, tudo numa só aplicação",
    intro: "Técnicos que visitam vários clientes por dia, recibos no porta-luvas, quilometragem registada de memória. A Done-it captura as horas, os quilómetros e as despesas no terreno — associados ao serviço certo no momento em que acontecem.",
    pains: [
      {
        title: "Quilometragem registada dias depois — quando muito",
        body: "Pedir aos técnicos que reconstruam os seus percursos no fim da semana significa reembolsos imprecisos, disputas com os clientes e uma pilha crescente de quilómetros não reclamados.",
      },
      {
        title: "Despesas em recibos de papel que desaparecem",
        body: "Um recibo no porta-luvas é um custo que pode nunca chegar a entrar numa fatura. O custo real por serviço fica sempre subestimado.",
      },
      {
        title: "Sem visão em tempo real de onde está a equipa",
        body: "Sem check-ins em tempo real, só descobre onde está um técnico quando algo corre mal e alguém liga.",
      },
      {
        title: "Difícil ver a margem real por serviço",
        body: "Quando a mão de obra, as deslocações e as despesas são acompanhadas em sistemas separados — ou não são acompanhadas de todo — nunca existe um único valor de custo por serviço.",
      },
    ],
    features: [
      {
        title: "Captura automática de quilometragem (Trax-it)",
        body: "O GPS regista a partida, a chegada e a distância exata por viagem. Configure tipos de transporte (carro próprio, viatura da empresa, boleia) com as suas próprias taxas de reembolso. Sem caderno de bordo manual.",
        plan: "Add-on",
      },
      {
        title: "Gestão de despesas na obra",
        body: "Os trabalhadores fotografam um recibo no terreno; fica associado ao serviço e pronto para reembolso — sem ter de andar atrás dele no fecho do mês.",
        plan: "Time Basic",
      },
      {
        title: "Controlo de custos associado ao projeto",
        body: "As horas, a quilometragem e as despesas acumulam-se no serviço em tempo real. O custo por serviço fica visível enquanto o serviço ainda está em aberto.",
        plan: "Project Basic",
      },
      {
        title: "Check-in com verificação de localização e visão geral da equipa em tempo real",
        body: "Cada técnico faz check-in no local do cliente. Vê quem está onde, neste momento — sem chamadas, sem adivinhação.",
      },
      {
        title: "Exportação salarial com um clique",
        body: "As horas e os reembolsos de quilometragem exportam de forma limpa para o seu prestador de processamento salarial — com as suas taxas de quilometragem configuradas aplicadas automaticamente.",
      },
    ],
    compliance: {
      label: "Registo digital de tempo e quilometragem",
      title: "Horas por GPS e quilometragem automática para equipas de assistência técnica no terreno",
      body: "A Done-it captura a quilometragem automaticamente por viagem, aplica as suas taxas de reembolso configuradas e regista cada quilómetro ao cliente ou projeto certo. O acórdão Tyco da UE significa que a deslocação de casa ao primeiro cliente conta como tempo de trabalho — a Done-it acompanha isso também.",
      slug: "how-to-track-mileage-for-field-teams",
    },
    workflow: [
      { step: "O técnico parte para a primeira intervenção", body: "O check-in inicia o registo da viagem. Os quilómetros são capturados automaticamente da partida à chegada — sem introdução manual." },
      { step: "No local do serviço", body: "As horas associam-se ao projeto certo. O trabalho extra, os materiais e os recibos são registados na aplicação no momento em que acontecem." },
      { step: "Fecho do mês sem o caos", body: "Cada hora, quilómetro e despesa já está associada ao serviço correto. Exporte para o salário e fature com valores de custo reais." },
    ],
    stats: [
      { value: "Automática", label: "captura de quilometragem — sem caderno de bordo manual" },
      { value: "Tempo real", label: "custo por serviço: horas + deslocações + despesas" },
      { value: "0", label: "quilómetros perdidos — cada viagem capturada automaticamente" },
    ],
    quote: {
      text: "A quilometragem costumava ser uma dor de cabeça mensal. Agora regista-se sozinha — cada quilómetro está associado ao cliente certo antes mesmo de regressarmos ao escritório.",
      who: "Gestor de operações, empresa de assistência técnica no terreno · 11 colaboradores",
      result: "Custo por serviço exato, sempre",
    },
    relatedSlugs: ["hvac", "maintenance", "plumbing"],
    relatedArticles: [
      "how-to-track-mileage-for-field-teams",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },

  {
    slug: "hvac",
    name: "AVAC",
    headline: "Registo de tempo para técnicos de AVAC — cálculo de custos por serviço, peças e deslocações numa só aplicação",
    intro: "Cada intervenção de assistência tem três custos: mão de obra, peças e deslocação. A Done-it captura os três no terreno e acumula-os no serviço certo — para que conheça a sua margem antes de fechar a fatura.",
    pains: [
      {
        title: "As intervenções são difíceis de orçamentar com precisão",
        body: "A mão de obra, as peças e a deslocação vivem em sistemas diferentes — ou em bolsos diferentes. O custo real de uma intervenção só fica claro muito depois de o técnico ter saído do local.",
      },
      {
        title: "Sem prova do tempo de serviço",
        body: "Quando um cliente contesta quanto tempo demorou uma reparação, uma folha de horas em papel é fácil de contestar. As horas de check-in e check-out confirmadas por GPS não são.",
      },
      {
        title: "Os contratos de manutenção corroem a margem em silêncio",
        body: "Nos contratos de manutenção recorrentes, pequenos excessos acumulam-se ao longo de muitas visitas. Sem um contador de horas em tempo real, só descobre que o contrato está no vermelho na renovação.",
      },
      {
        title: "Os técnicos esquecem-se de registar pausas",
        body: "Os registos de tempo manuais falham as pausas, levando a pagamentos a mais e a um salário que não corresponde à realidade.",
      },
    ],
    features: [
      {
        title: "Registo de tempo por projeto",
        body: "Cada intervenção ou contrato de manutenção é um projeto. As horas, as deslocações e os materiais acumulam-se em tempo real — dando-lhe visibilidade da margem em tempo real.",
        plan: "Project Basic",
      },
      {
        title: "Trabalho extra e materiais, registados na obra",
        body: "Quando um serviço cresce, os técnicos registam o trabalho extra e as peças com texto e fotos antes de saírem. Nada é faturado de memória.",
        plan: "Project Basic",
      },
      {
        title: "Custos de quilometragem e transporte",
        body: "Os quilómetros por intervenção são registados automaticamente e imputados ao serviço. Configure taxas de reembolso por tipo de viatura.",
        plan: "Add-on",
      },
      {
        title: "Dedução automática de pausas",
        body: "As pausas obrigatórias são subtraídas automaticamente do tempo total — sem disputas, sem erros salariais.",
      },
      {
        title: "Check-in com verificação de localização e prova de presença",
        body: "Check-in com data e hora, confirmado por GPS, em cada local. Cada registo é verificado por localização.",
      },
    ],
    compliance: {
      label: "Registo digital de tempo e custos",
      title: "Cálculo de custos completo para intervenções de AVAC",
      body: "A Done-it dá às equipas de AVAC check-in com verificação de localização por intervenção, visibilidade de custos em tempo real por serviço (mão de obra + deslocações + peças) e uma exportação salarial limpa — cumprindo qualquer requisito local de registo de tempo sem hardware adicional.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "O técnico faz check-in no local", body: "O GPS regista a hora da chegada e associa as horas à intervenção ou ao contrato de manutenção. Sem papel, sem terminal." },
      { step: "O trabalho extra é capturado no momento", body: "As peças usadas, as horas extra e as alterações de âmbito são registadas na aplicação com fotos — antes de sair do local." },
      { step: "O custo do serviço fica visível de imediato", body: "A mão de obra, as deslocações e os materiais somam-se ao projeto em tempo real. Vê a margem antes de emitir a fatura." },
    ],
    stats: [
      { value: "3", label: "tipos de custo capturados por intervenção: mão de obra, deslocações, peças" },
      { value: "Tempo real", label: "visibilidade da margem por contrato de assistência" },
      { value: "0", label: "surpresas na altura de faturar — custos visíveis em tempo real" },
    ],
    quote: {
      text: "Finalmente sabemos quanto cada contrato de assistência nos custa realmente. As surpresas na margem acabaram no dia em que começámos a usar a Done-it.",
      who: "Proprietário, empresa de AVAC · 7 técnicos",
      result: "Custo real por intervenção, em tempo real",
    },
    relatedSlugs: ["installation", "electricians", "plumbing"],
    relatedArticles: [
      "how-to-track-mileage-for-field-teams",
      "time-tracking-for-field-teams-complete-guide",
    ],
  },

  {
    slug: "electricians",
    name: "Eletricistas",
    headline: "Registo de tempo para eletricistas — horas por projeto, controlo de orçamento e navegação numa só aplicação",
    intro: "Horas por projeto, uma contagem decrescente em tempo real do orçamento restante e navegação para a obra com um botão — concebido para pequenas equipas de eletricidade que precisam de controlo de projeto sem a sobrecarga administrativa.",
    pains: [
      {
        title: "As horas não estão associadas ao projeto certo",
        body: "Quando os eletricistas trabalham em vários serviços numa semana, atribuir manualmente as horas ao fim do dia leva a custos mal imputados, disputas de faturação e um salário que não consegue explicar.",
      },
      {
        title: "Desvios de orçamento detetados demasiado tarde",
        body: "Sem uma visão em tempo real das horas consumidas vs. horas orçamentadas, só descobre que um projeto excedeu o previsto quando emite a fatura — não quando ainda podia reagir.",
      },
      {
        title: "Tempo perdido a encontrar a obra",
        body: "Moradas escritas numa aplicação separada, chamadas ao escritório a pedir direções, chegadas atrasadas a locais de clientes. Cada minuto desperdiçado é um minuto faturável perdido.",
      },
      {
        title: "Horas de subcontratados difíceis de reconciliar",
        body: "Em instalações maiores, os eletricistas subcontratados acompanham as suas próprias horas em separado. Obter uma visão combinada dos custos de mão de obra no fim de um projeto é uma tarefa manual e propensa a erros.",
      },
    ],
    features: [
      {
        title: "Horas por projeto com orçamento restante em tempo real",
        body: "Cada minuto trabalhado é associado ao projeto certo automaticamente. Uma contagem decrescente em tempo real mostra quantas horas restam face ao orçamento — para si e para o trabalhador na obra.",
        plan: "Project Basic",
      },
      {
        title: "Navegação para a obra com um botão",
        body: "A morada do projeto está na aplicação. Um toque abre a navegação — sem procuras, sem aplicação de mapas separada, sem chamadas ao escritório.",
      },
      {
        title: "Check-in do trabalhador por projeto",
        body: "Cada eletricista faz check-in no serviço certo. Sabe sempre quem está em que obra neste momento.",
        plan: "Project Basic",
      },
      {
        title: "Trabalho extra registado na obra",
        body: "Quando o âmbito muda — um circuito inesperado, materiais adicionais — fica registado na aplicação com fotos, antes de a equipa sair.",
        plan: "Project Basic",
      },
      {
        title: "Registo de horas extra com compensação flexível",
        body: "As horas extra são registadas no momento em que acontecem. Os trabalhadores escolhem que sejam pagas ou convertidas em tempo de folga.",
        plan: "Time Pro",
      },
    ],
    compliance: {
      label: "Check-in com verificação de localização por projeto",
      title: "Controlo de orçamento em tempo real e check-in com verificação de localização para equipas de eletricidade",
      body: "A Done-it dá aos eletricistas check-in com verificação de localização por projeto, um orçamento de horas restantes em tempo real e exportação salarial — satisfazendo qualquer requisito local de registo de tempo. Para equipas em obras de construção belgas, integra também com o CIAO@work.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "O eletricista navega para o serviço", body: "A morada e o âmbito já estão na aplicação. Um toque abre a navegação — sem chamadas, sem confusão." },
      { step: "As horas associam-se ao projeto certo automaticamente", body: "O check-in no local associa cada minuto trabalhado ao projeto correto. O contador de horas restantes atualiza-se em tempo real." },
      { step: "As alterações de âmbito ficam registadas", body: "O trabalho extra e os materiais são registados na obra, antes de sair. A fatura reflete o que realmente aconteceu." },
    ],
    stats: [
      { value: "Tempo real", label: "vista de horas restantes por projeto" },
      { value: "0", label: "chamadas ao escritório a pedir moradas de obras" },
      { value: "1 toque", label: "para fazer check-in em qualquer obra — sem terminal" },
    ],
    quote: {
      text: "Sabemos em tempo real se um projeto está a exceder o orçamento. Só isso nos salvou de duas surpresas más este ano.",
      who: "Proprietário, empresa de eletricidade · 5 eletricistas",
      result: "Desvios de orçamento detetados antes de acontecerem",
    },
    relatedSlugs: ["installation", "hvac", "plumbing"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "how-to-track-mileage-for-field-teams",
    ],
  },

  {
    slug: "plumbing",
    name: "Canalização",
    headline: "Registo de tempo para equipas de canalização — check-in rápido, salário exato e prova de presença em cada intervenção",
    intro: "Intervenções de emergência, vários serviços por dia, horas preenchidas de memória no fim da semana — ou de todo. A Done-it transforma isto num trabalho de um toque em cada local e num salário de um clique no fecho do mês.",
    pains: [
      {
        title: "Horas de intervenção reconstruídas de memória",
        body: "Com vários serviços num dia, os canalizadores raramente têm tempo para preencher folhas de horas exatas. As horas são arredondadas ou esquecidas, e o salário é, na melhor das hipóteses, uma estimativa.",
      },
      {
        title: "Sem prova do tempo no local",
        body: "Quando um cliente contesta quanto tempo demorou uma reparação, não há registo objetivo. Um check-in digital confirmado por GPS é fácil de apresentar — uma folha de horas em papel não é.",
      },
      {
        title: "Quilometragem entre serviços não acompanhada",
        body: "As deslocações entre intervenções acumulam-se ao longo da semana e do mês, mas sem captura automática raramente são faturadas ou reembolsadas corretamente.",
      },
      {
        title: "A preparação do salário é lenta",
        body: "No fecho do mês, andar atrás das folhas de horas, corrigir erros e construir a exportação salarial leva tempo que a sua equipa administrativa não devia estar a gastar.",
      },
    ],
    features: [
      {
        title: "Check-in com verificação de localização e um toque em cada intervenção",
        body: "Chegada e partida registadas com um toque, com verificação de localização do local. Rápido o suficiente para chamadas de emergência; preciso o suficiente para disputas.",
        plan: "Time Basic",
      },
      {
        title: "Dedução automática de pausas",
        body: "As pausas obrigatórias são subtraídas do tempo total automaticamente — nada para preencher, sem erros salariais.",
        plan: "Time Basic",
      },
      {
        title: "Quilometragem capturada por viagem",
        body: "Configure tipos de transporte e taxas de reembolso. Os quilómetros registam-se automaticamente e associam-se ao cliente ou projeto correto.",
        plan: "Add-on",
      },
      {
        title: "Gestão de ausências e justificações de baixa",
        body: "Os trabalhadores submetem justificações de baixa digitalmente a partir da aplicação. Crie os tipos de ausência que a sua equipa realmente utiliza, incluindo paragens por mau tempo.",
      },
      {
        title: "Exportação salarial com um clique",
        body: "Todos os dados de horas e quilometragem exportam de forma limpa para o seu prestador de processamento salarial — sem preparação manual, sem Excel.",
        plan: "Time Basic",
      },
    ],
    compliance: {
      label: "Registo de tempo digital",
      title: "Check-in rápido com verificação de localização para cada intervenção",
      body: "A Done-it cobre qualquer requisito local de registo de tempo para equipas de canalização — check-in com verificação de localização e um toque por intervenção, dedução automática de pausas e uma exportação salarial limpa. Sem folhas de horas em papel, sem reconstrução manual.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "O canalizador chega à intervenção", body: "Um toque faz check-in. O GPS regista o local e a hora. Rápido o suficiente para um serviço de emergência." },
      { step: "Partida registada à saída", body: "O check-out fecha o registo de tempo. A quilometragem para o próximo serviço começa a ser registada automaticamente." },
      { step: "O fecho do mês é um clique", body: "Todas as intervenções, horas e quilómetros exportam de forma limpa. A preparação do salário passa de um dia para minutos." },
    ],
    stats: [
      { value: "1 toque", label: "para fazer check-in — rápido o suficiente para chamadas de emergência" },
      { value: "1 clique", label: "exportação salarial para o seu prestador" },
      { value: "0", label: "horas reconstruídas de memória no fecho do mês" },
    ],
    quote: {
      text: "Costumávamos passar uma manhã no fecho do mês a reconstruir horas. Agora basta exportar e enviar. O prestador de processamento salarial recebe tudo limpo.",
      who: "Proprietário, empresa de canalização · 4 canalizadores",
      result: "Preparação do salário reduzida de horas para minutos",
    },
    relatedSlugs: ["hvac", "electricians", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "how-to-track-mileage-for-field-teams",
    ],
  },

  {
    slug: "landscaping",
    name: "Jardinagem e paisagismo",
    headline: "Registo de tempo para empresas de jardinagem e paisagismo — equipas sazonais, vários locais e quilometragem numa só visão geral",
    intro: "A época alta significa muitos locais, equipas que mudam e muitos quilómetros. A Done-it mantém cada hora, cada ausência e cada viagem organizadas — para que se possa focar no trabalho, e não na papelada.",
    pains: [
      {
        title: "As equipas sazonais são complexas de gerir",
        body: "Trabalhadores temporários, horários que mudam e equipas de dimensão variável tornam o registo de horas exato quase impossível com papel. Quando reconcilia no fecho do mês, está sempre a faltar alguma coisa.",
      },
      {
        title: "Horas espalhadas por muitos locais",
        body: "Uma equipa que visita três ou quatro locais por dia gera horas que precisam de ser divididas corretamente. A introdução manual leva a má imputação e a salários contestados.",
      },
      {
        title: "As paragens por mau tempo são difíceis de documentar",
        body: "A paragem por mau tempo é um evento real na jardinagem e paisagismo. Sem um registo digital, documentar as paragens e ajustar o pagamento é lento e propenso a erros.",
      },
      {
        title: "A quilometragem entre locais fica por acompanhar",
        body: "Carrinhas da empresa, viaturas pessoais e boleias cruzam-se entre locais durante todo o dia. Sem captura automática, esses quilómetros nunca chegam a entrar na imagem de custos.",
      },
    ],
    features: [
      {
        title: "Visão geral da equipa em vários locais",
        body: "Veja toda a equipa, todos os locais em tempo real. Saiba quem está a trabalhar onde, neste momento — e não no fecho do mês.",
      },
      {
        title: "Planeamento de turnos para equipas sazonais",
        body: "Planeie quem trabalha onde, lide com alterações de última hora e envie o horário para os telemóveis da equipa — sem um telefonema a cada pessoa.",
        plan: "Time Pro",
      },
      {
        title: "Tipos de ausência personalizados",
        body: "Crie tipos de ausência personalizados para o seu setor: paragens sazonais por mau tempo, licença parental, dias de baixa. Os trabalhadores carregam a documentação digitalmente a partir da aplicação.",
      },
      {
        title: "Quilometragem com registo de boleias",
        body: "Configure tipos de transporte (carrinha da empresa, carro pessoal, condutor vs. passageiro em boleia) com as suas próprias taxas. Os quilómetros registam-se automaticamente por viagem.",
        plan: "Add-on",
      },
      {
        title: "Registo de horas extra com conversão em folga",
        body: "As horas extra de época alta acumulam-se depressa. Cada trabalhador pode escolher ser pago ou acumular as horas como tempo de folga — acompanhado de forma transparente.",
        plan: "Time Pro",
      },
    ],
    compliance: {
      label: "Registo de tempo digital",
      title: "Check-in com verificação de localização e registo de ausências para equipas sazonais de jardinagem e paisagismo",
      body: "A Done-it dá às equipas de jardinagem e paisagismo registo de tempo digital em todos os locais e pessoal sazonal — check-in com verificação de localização, tipos de ausência personalizados para paragens por mau tempo e uma exportação salarial limpa que satisfaz qualquer requisito local.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "A equipa faz check-in no primeiro local", body: "O GPS regista a chegada. As horas dividem-se automaticamente pelo local e projeto certos — sem atribuição manual ao fim do dia." },
      { step: "O mau tempo para o trabalho", body: "O encarregado regista a paragem a partir da aplicação — um registo digital limpo, sem papelada extra." },
      { step: "Fecho do mês: todos os locais, toda a equipa, uma exportação", body: "As horas, as ausências e a quilometragem em cada local e de cada membro da equipa exportam de forma limpa para o salário." },
    ],
    stats: [
      { value: "Automática", label: "captura de quilometragem — sem mais caderno de bordo" },
      { value: "Tempo real", label: "visão geral da equipa em todos os locais" },
      { value: "1 clique", label: "exportação salarial para todos os locais e toda a equipa" },
    ],
    quote: {
      text: "A época alta costumava significar caos no salário. Agora o nosso encarregado consulta a aplicação, está toda a gente contabilizada, e eu exporto as horas no último dia do mês.",
      who: "Proprietário, empresa de jardinagem e paisagismo · 12 trabalhadores sazonais",
      result: "Sem mais reconstrução salarial no fim do mês",
    },
    relatedSlugs: ["maintenance", "construction", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },

  {
    slug: "maintenance",
    name: "Manutenção",
    headline: "Registo de tempo para equipas de manutenção — visitas recorrentes a locais, custo por contrato e prova de presença numa só aplicação",
    intro: "Os contratos de manutenção recorrentes vivem ou morrem da visibilidade de custos. A Done-it regista cada visita, acompanha as horas face ao orçamento do contrato e dá-lhe a si — e aos seus clientes — prova de presença em cada intervenção.",
    pains: [
      {
        title: "Sem visibilidade de custos por contrato de manutenção",
        body: "Quando as horas das visitas recorrentes não são acompanhadas face ao contrato específico, não sabe se um cliente é rentável até à revisão de fim de ano — altura em que já é tarde demais para rever os preços.",
      },
      {
        title: "Os clientes esperam prova do que foi feito",
        body: "Os gestores de instalações e os proprietários querem cada vez mais registos de presença: quem veio, quando chegou, o que fez. Um registo em papel apresentado semanas depois não satisfaz os requisitos de SLA.",
      },
      {
        title: "Visitas recorrentes difíceis de agendar e registar",
        body: "Turnos rotativos por muitos locais recorrentes — com pessoal que se substitui mutuamente — criam um quebra-cabeças de agendamento e de registo de horas que os processos manuais não conseguem resolver de forma fiável.",
      },
      {
        title: "As paragens por mau tempo afetam também a manutenção no exterior",
        body: "A manutenção de espaços verdes, de fachadas e de instalações exteriores enfrenta paragens relacionadas com o tempo. Documentá-las corretamente exige um rasto digital.",
      },
    ],
    features: [
      {
        title: "Registo de custos por contrato",
        body: "Cada contrato de manutenção é um projeto. As horas registam-se automaticamente nele, dando-lhe um valor de custo em tempo real e uma contagem decrescente de horas restantes para cada cliente.",
        plan: "Project Basic",
      },
      {
        title: "Prova de presença confirmada por GPS",
        body: "Os check-ins com data e hora, verificados por localização, são um registo de quem esteve no local, quando e durante quanto tempo — prontos a partilhar com os clientes a pedido.",
      },
      {
        title: "Atualizações com fotos a partir do local",
        body: "Os trabalhadores reportam problemas, tarefas concluídas ou trabalho extra diretamente a partir da aplicação — com texto e fotos. Os clientes recebem um registo em tempo real; o gestor tem a documentação para faturar.",
        plan: "Project Basic",
      },
      {
        title: "Planeamento de turnos recorrentes",
        body: "Planeie as visitas recorrentes em todos os contratos, atribua trabalhadores e lide com substituições — sem um telefonema a cada pessoa.",
        plan: "Time Pro",
      },
      {
        title: "Tipos de ausência para manutenção no exterior",
        body: "Configure paragens por mau tempo e outros tipos de ausência. Os trabalhadores registam-nas a partir da aplicação; a documentação carrega-se digitalmente.",
      },
    ],
    compliance: {
      label: "Registo de tempo por contrato",
      title: "Visibilidade de custos em tempo real por contrato de manutenção",
      body: "A Done-it dá às equipas de manutenção prova de presença confirmada por GPS por contrato, tipos de ausência personalizados e uma vista de custos em tempo real por cliente recorrente — satisfazendo qualquer requisito local de registo de tempo sem hardware adicional.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "O trabalhador chega ao local de manutenção", body: "O check-in com verificação de localização regista a hora da chegada e associa as horas ao contrato correto. A prova de presença é criada automaticamente." },
      { step: "Tarefas concluídas, problemas registados", body: "O trabalho concluído e quaisquer problemas são reportados a partir da aplicação com fotos. Os clientes conseguem ver exatamente o que foi feito." },
      { step: "Custo por contrato visível de imediato", body: "As horas acumulam-se face ao orçamento do contrato em tempo real. Conhece a margem antes de o contrato renovar." },
    ],
    stats: [
      { value: "Tempo real", label: "custo por contrato de manutenção" },
      { value: "GPS", label: "prova de presença pronta para os clientes a pedido" },
      { value: "0", label: "renovações de contrato sem conhecer a sua margem" },
    ],
    quote: {
      text: "Conseguimos dizer a um gestor de instalações exatamente quem esteve no local e quando, em dois minutos. Esse nível de prova mudou a forma como os clientes nos veem.",
      who: "Gestor de operações, empresa de manutenção · 8 trabalhadores",
      result: "Disputas com clientes reduzidas a zero",
    },
    relatedSlugs: ["cleaning", "landscaping", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },
  {
    slug: "retail",
    name: "Lojas e retalho",
    headline: "Registo de tempo para lojas — picagem de turnos, substituições e exportação pronta para salários, numa só aplicação",
    intro: "Turnos de abertura, turnos de fecho e há sempre alguém a substituir alguém. A Done-it dá a cada loja um registo digital limpo de quem fez que turno — picado no próprio telemóvel, pronto para o processamento salarial ao fim do mês.",
    pains: [
      {
        title: "As trocas de turno nunca chegam aos salários",
        body: "Alguém substitui um colega doente, alguém fica mais uma hora no fecho. Fica anotado num papel, ou não fica de todo — e os salários já estão errados antes de começar.",
      },
      {
        title: "Várias lojas, um só processamento salarial",
        body: "Os colaboradores circulam entre lojas, os de tempo parcial trabalham em dois locais. Recolher as horas loja a loja e juntá-las à mão custa um dia por mês.",
      },
      {
        title: "Pausas e horas extraordinárias são adivinhação",
        body: "As pausas obrigatórias são descontadas de cor e as horas a mais acertadas informalmente — as duas primeiras coisas que uma inspeção pergunta.",
      },
    ],
    features: [
      {
        title: "Picar o ponto na própria loja",
        body: "Um toque no telemóvel no início do turno. Sem terminal junto à caixa, sem cartão partilhado, sem folha à porta do armazém.",
      },
      {
        title: "Horas por loja, equipa entre lojas",
        body: "Cada loja é um projeto. Quem substitui noutra loja fica registado nessa loja — vê as horas onde foram efetivamente trabalhadas.",
      },
      {
        title: "Pausas descontadas automaticamente",
        body: "Define a regra de pausas uma vez e aplica-se a todos os turnos. As horas extraordinárias ficam registadas no momento e cada colaborador escolhe entre pagamento ou descanso.",
      },
    ],
    compliance: {
      label: "Registo de tempo por turnos",
      title: "Um registo defensável de cada turno trabalhado",
      body: "A Done-it mantém um registo digital e objetivo de cada entrada e saída, com pausas e horas extraordinárias — o registo que uma auditoria ou um litígio salarial exige.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "A equipa pica o ponto no turno", body: "Um toque no telemóvel quando o turno começa. A substituição noutra loja fica registada nessa loja." },
      { step: "Vê quem está ao serviço, loja a loja", body: "Uma vista em tempo real de todos os locais — deteta um turno de abertura por preencher antes de abrir as portas." },
      { step: "Salários numa só exportação", body: "Horas, pausas e horas extraordinárias por colaborador e por loja, exportadas prontas para o seu processador salarial." },
    ],
    stats: [
      { value: "1 toque", label: "para picar o ponto — sem terminal na caixa" },
      { value: "Em direto", label: "quem está ao serviço, em todas as lojas" },
      { value: "0", label: "trocas de turno perdidas entre a loja e os salários" },
    ],
    quote: {
      text: "Duas lojas e muita gente a tempo parcial. Antes, metade das substituições era acertada de memória. Agora cada hora fica na loja certa e o processamento salarial demora minutos.",
      who: "Proprietária, retalho · 11 colaboradores",
      result: "Preparação salarial de um dia para menos de uma hora",
    },
    relatedSlugs: ["hospitality", "beauty", "offices"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "offices",
    name: "Escritórios e serviços profissionais",
    headline: "Registo de tempo para escritórios — horas por cliente e projeto, sem folha de cálculo",
    intro: "Contabilistas, notários, arquitetos, agências e consultoras faturam o seu tempo — ou, no mínimo, precisam de saber para onde foi. A Done-it regista as horas no cliente e no projeto certos, da secretária ou fora do escritório.",
    pains: [
      {
        title: "Horas faturáveis reconstruídas à sexta-feira",
        body: "Tempo apontado no fim da semana é tempo adivinhado. Horas inteiras desaparecem discretamente entre o trabalho e a fatura.",
      },
      {
        title: "Não faz ideia do que um cliente custa",
        body: "Os honorários foram acordados há meses. Se o trabalho ainda cabe lá dentro só se percebe quando alguém faz as contas — normalmente tarde de mais.",
      },
      {
        title: "O trabalho híbrido deixa o registo cheio de buracos",
        body: "Parte da equipa está no escritório, parte em casa, parte no cliente. A assiduidade deixa de ser um facto e passa a ser uma reconstrução.",
      },
    ],
    features: [
      {
        title: "Horas associadas a cliente e projeto",
        body: "Cada registo cai num projeto, por isso vê as horas por cliente sem exportar nada nem perguntar a ninguém.",
      },
      {
        title: "Funciona na secretária e no exterior",
        body: "A mesma aplicação no telemóvel ou no browser. Visitar um cliente conta como trabalho nesse cliente, incluindo a deslocação se assim o quiser.",
      },
      {
        title: "Horas extraordinárias e férias no mesmo sítio",
        body: "Horas a mais registadas na hora, férias pedidas e aprovadas na aplicação — sem folha de cálculo paralela de quem está ausente e quando.",
      },
    ],
    compliance: {
      label: "Registo de tempo por cliente",
      title: "Um registo objetivo das horas trabalhadas",
      body: "A Done-it mantém um registo digital e inviolável do tempo de trabalho por colaborador — a base tanto para os salários como para uma fatura ao cliente que se sustenta.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Abra um projeto de cliente", body: "Configure-o uma vez com o cliente e, se fatura por orçamento, com as horas previstas." },
      { step: "A equipa regista o tempo nele", body: "Da secretária ou do telemóvel. Cada hora cai num cliente, não na memória de alguém." },
      { step: "Veja as horas por cliente, exporte para salários", body: "Totais em tempo real por projeto e uma exportação limpa para salários ou faturação no fim do mês." },
    ],
    stats: [
      { value: "Por cliente", label: "horas visíveis enquanto o trabalho decorre" },
      { value: "Escritório ou rua", label: "o mesmo registo, telemóvel e browser" },
      { value: "1 exportação", label: "para salários e faturação" },
    ],
    quote: {
      text: "Antes reconstruíamos a semana toda às sextas-feiras. Agora as horas já estão no cliente certo e vejo quais são os processos que estão a comer os honorários.",
      who: "Sócio, gabinete de contabilidade · 8 colaboradores",
      result: "Horas faturáveis recuperadas todos os meses",
    },
    relatedSlugs: ["retail", "healthcare", "wellness"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "beauty",
    name: "Salões e cuidados de beleza",
    headline: "Registo de tempo para salões — turnos, horas a tempo parcial e salários sem a agenda em papel",
    intro: "Cabeleireiros, barbearias, estúdios de unhas e de estética vivem de turnos e contratos a tempo parcial. A Done-it regista quem trabalhou quando, desconta as pausas automaticamente e entrega aos salários um ficheiro limpo.",
    pains: [
      {
        title: "Contratos a tempo parcial, variações sem fim",
        body: "Três dias numa semana, quatro na seguinte, um sábado trocado por uma terça-feira. As horas contratadas e as horas trabalhadas afastam-se depressa.",
      },
      {
        title: "A agenda não é um registo de tempo",
        body: "As marcações dizem o que foi reservado, não quem trabalhou. Quando os salários ou uma inspeção perguntam, o sistema de marcações é a resposta errada.",
      },
      {
        title: "Horas a mais acertadas informalmente",
        body: "Ficar até mais tarde para o último cliente fica na memória, é prometido e às vezes esquecido. É esse tipo de falha que se transforma em litígio.",
      },
    ],
    features: [
      {
        title: "Picar o ponto no salão",
        body: "Um toque no próprio telemóvel no início do turno — sem terminal partilhado na receção, sem folha de assinaturas ao lado do espelho.",
      },
      {
        title: "Pausas e regras de tempo parcial, uma só vez",
        body: "Defina a regra de pausas e o horário normal uma vez; aplicam-se a todos os turnos, com exceções por pessoa quando o contrato é diferente.",
      },
      {
        title: "Horas extraordinárias, pagas ou compensadas",
        body: "As horas a mais são registadas à medida que são trabalhadas e cada colaborador escolhe entre pagamento ou descanso compensatório — acertado na aplicação, não de cor.",
      },
    ],
    compliance: {
      label: "Registo de tempo digital",
      title: "Um registo que se sustenta para lá do livro de marcações",
      body: "A Done-it regista o tempo de trabalho efetivo por colaborador, com pausas e horas extraordinárias — separado das marcações e defensável quando é verificado.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "A equipa pica o ponto no turno", body: "Um toque à chegada. As pausas são descontadas pela regra que definiu uma vez." },
      { step: "Vê o dia enquanto ele acontece", body: "Quem está ao serviço, quem está em pausa, quem ficou até mais tarde — em direto, sem perguntar." },
      { step: "Os salários recebem um ficheiro limpo", body: "Horas trabalhadas, pausas e horas extraordinárias por colaborador, exportadas para o seu processador salarial." },
    ],
    stats: [
      { value: "1 toque", label: "para picar o ponto — nada partilhado, nada em papel" },
      { value: "Auto", label: "desconto de pausas em todos os turnos" },
      { value: "0", label: "horas tardias acertadas de memória" },
    ],
    quote: {
      text: "Toda a gente está a tempo parcial e cada semana é diferente. A aplicação limita-se a registar — deixei de reconstruir o mês numa folha de cálculo.",
      who: "Proprietária, salão de cabeleireiro · 6 colaboradoras",
      result: "Acabaram as horas contestadas no fim do mês",
    },
    relatedSlugs: ["wellness", "retail", "hospitality"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "wellness",
    name: "Saúde e bem-estar",
    headline: "Registo de tempo para ginásios, estúdios e spas — turnos, aulas e salários numa só aplicação",
    intro: "Ginásios, estúdios de fitness, clínicas de fisioterapia e spas têm horários alargados com equipas rotativas. A Done-it regista quem cobriu que turno, desconta as pausas automaticamente e exporta os salários sem confusões.",
    pains: [
      {
        title: "Horários alargados, substituições rotativas",
        body: "Turnos de manhã cedo, aulas à noite e fins de semana, preenchidos por uma mistura de tempo inteiro, tempo parcial e recibos verdes. A escala é um plano, não um registo.",
      },
      {
        title: "As horas de aula e as de sala confundem-se",
        body: "Um instrutor dá duas aulas e fica na receção entre elas. Pagar isso corretamente exige saber o que foi mesmo trabalhado, não o que estava previsto.",
      },
      {
        title: "Vários locais, uma só administração",
        body: "As pessoas circulam entre estúdios. Juntar as horas à mão todos os meses é lento e propício a erros.",
      },
    ],
    features: [
      {
        title: "Picar o ponto por estúdio",
        body: "Um toque no telemóvel no local onde estão a trabalhar. A substituição noutro estúdio entra nas horas desse estúdio.",
      },
      {
        title: "Vista em direto de quem está ao serviço",
        body: "Veja a sala em todos os locais em tempo real — um turno de manhã por preencher fica visível antes de abrir as portas, não depois.",
      },
      {
        title: "Pausas e horas extraordinárias tratadas",
        body: "Regras de pausa aplicadas automaticamente, horas a mais registadas no momento com pagamento ou descanso compensatório por colaborador.",
      },
    ],
    compliance: {
      label: "Registo de turnos multilocal",
      title: "Um registo objetivo para cada turno e cada local",
      body: "A Done-it mantém um registo digital do tempo de trabalho por colaborador e por local — a base para pagar corretamente e para qualquer verificação das horas trabalhadas.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "A equipa pica o ponto no estúdio", body: "Um toque no início do turno, no local onde está a trabalhar." },
      { step: "As substituições ficam visíveis em direto", body: "Quem está em cada local neste momento, para que as falhas sejam preenchidas antes de os sócios darem por elas." },
      { step: "Uma exportação para os salários", body: "Horas, pausas e horas extraordinárias por colaborador e por estúdio, prontas para o processador salarial." },
    ],
    stats: [
      { value: "Por estúdio", label: "horas, mesmo quando a equipa substitui noutro local" },
      { value: "Em direto", label: "vista da sala em todos os locais" },
      { value: "Auto", label: "pausas e horas extraordinárias, sem contas à mão" },
    ],
    quote: {
      text: "Dois estúdios, horários alargados e muitas substituições. Agora as horas caem no local certo e os salários são um só ficheiro.",
      who: "Gestor, estúdio de fitness · 14 colaboradores",
      result: "Escala e salários finalmente coincidem",
    },
    relatedSlugs: ["beauty", "healthcare", "hospitality"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "hospitality",
    name: "Restauração e hotelaria",
    headline: "Registo de tempo para cafés, restaurantes e hotéis — turnos, pausas e horas extraordinárias, acertados com justiça",
    intro: "Horários repartidos, fechos tardios e um sábado cheio que se prolonga. A Done-it regista as horas tal como são trabalhadas, desconta as pausas pela sua regra e entrega aos salários um único ficheiro limpo.",
    pains: [
      {
        title: "O turno nunca acaba à hora que a escala diz",
        body: "O serviço prolonga-se, o fecho leva mais meia hora. Esses minutos são trabalho real, e acertá-los de memória é como começam os litígios.",
      },
      {
        title: "Os horários repartidos são difíceis de registar",
        body: "Serviço de almoço, uma pausa, depois o jantar. No papel vira um bloco único ou dois palpites — nenhum deles é a verdade.",
      },
      {
        title: "Muita rotatividade, integração constante",
        body: "Pessoal novo a cada época. Qualquer sistema que exija formação não sobrevive à primeira semana cheia.",
      },
    ],
    features: [
      {
        title: "Entradas e saídas por serviço",
        body: "Um toque no início e no fim de cada bloco, para que os horários repartidos fiquem registados como aconteceram — sem serem fundidos num só.",
      },
      {
        title: "Pausas pela sua regra",
        body: "Pausas obrigatórias descontadas automaticamente, com exceções por pessoa quando o contrato o exige. Sem contas à mão no fim do mês.",
      },
      {
        title: "Horas extraordinárias acertadas na aplicação",
        body: "A meia hora a mais depois do fecho fica registada no momento em que é trabalhada e é paga ou compensada com descanso — o colaborador escolhe.",
      },
    ],
    compliance: {
      label: "Registo de horários repartidos",
      title: "Um registo defensável das horas numa equipa com muita rotatividade",
      body: "A Done-it mantém um registo digital e objetivo de cada turno, pausa e hora extraordinária — exatamente o que uma verificação salarial ou uma inspeção do trabalho pede para ver.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "A equipa pica o ponto para o serviço", body: "Um toque no início do bloco; o mesmo no fim. Os horários repartidos continuam repartidos." },
      { step: "Vê a sala em direto", body: "Quem está ao serviço esta noite, quem ainda está em pausa, quem ficou para lá do fecho." },
      { step: "Os salários recebem as horas reais", body: "Tempo trabalhado, pausas e horas extraordinárias por colaborador, exportados num só ficheiro." },
    ],
    stats: [
      { value: "Horários repartidos", label: "registados como dois blocos, não como um palpite" },
      { value: "Auto", label: "desconto de pausas pela sua regra" },
      { value: "1 min", label: "para integrar alguém novo — é o telemóvel dele" },
    ],
    quote: {
      text: "O fecho prolonga-se sempre. Agora esses minutos ficam registados em vez de discutidos, e a equipa pode recuperá-los como descanso.",
      who: "Proprietário, restaurante · 17 colaboradores",
      result: "Menos discussões, salários mais rápidos",
    },
    relatedSlugs: ["retail", "wellness", "beauty"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "healthcare",
    name: "Saúde e cuidados",
    headline: "Registo de tempo para equipas de cuidados — turnos, visitas domiciliárias e qualificações, num só registo",
    intro: "Clínicas, equipas de apoio domiciliário e lares funcionam à volta de turnos, visitas e regras rígidas de documentação. A Done-it regista o tempo trabalhado por turno e por visita, e guarda os documentos dos colaboradores onde os consegue encontrar.",
    pains: [
      {
        title: "Visitas e turnos em sistemas separados",
        body: "As visitas domiciliárias vivem numa ferramenta, as horas de escala noutra, e os salários recebem uma terceira versão. Conciliá-las é um trabalho mensal.",
      },
      {
        title: "Horas noturnas e de fim de semana mal pagas",
        body: "O trabalho fora do horário normal tem taxas diferentes. Quando o registo é aproximado, o pagamento também é — e a correção só chega depois.",
      },
      {
        title: "Os certificados caducam em silêncio",
        body: "As qualificações e os certificados contam nos cuidados de saúde, e ficam numa pasta que ninguém abre até uma inspeção perguntar.",
      },
    ],
    features: [
      {
        title: "Picar o ponto por turno ou por visita",
        body: "Um toque no início do turno, ou em cada morada de cliente no apoio domiciliário — com localização verificada onde precisa da prova.",
      },
      {
        title: "Horas separadas como as paga",
        body: "As horas noturnas, de fim de semana e normais são registadas em separado, para que a exportação salarial já reflita como o trabalho é efetivamente pago.",
      },
      {
        title: "Documentos dos colaboradores num só lugar",
        body: "Contratos, certificados e documentos de identificação guardados junto da pessoa a que pertencem — encontrados em segundos quando alguém os pede.",
      },
    ],
    compliance: {
      label: "Registo de turnos e visitas",
      title: "Registos que resistem a uma inspeção",
      body: "A Done-it mantém um registo digital e objetivo do tempo de trabalho por colaborador — horas, pausas e separação de horas fora do horário normal — a par dos documentos associados a cada pessoa.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Picar o ponto no turno ou na visita", body: "Um toque no local ou na morada do cliente, com localização verificada onde é preciso prova." },
      { step: "As horas caem na categoria certa", body: "Horas normais, noturnas e de fim de semana registadas em separado à medida que acontecem." },
      { step: "Exportar para os salários", body: "Um ficheiro limpo por período, já separado tal como o paga." },
    ],
    stats: [
      { value: "Por visita", label: "ou por turno — como a sua equipa funcionar" },
      { value: "Separadas", label: "horas noturnas e de fim de semana, registadas como trabalhadas" },
      { value: "Arquivados", label: "certificados guardados por colaborador" },
    ],
    quote: {
      text: "As visitas domiciliárias e os turnos de escala estão finalmente no mesmo registo. Os salários deixaram de ser um exercício de conciliação.",
      who: "Coordenadora, equipa de apoio domiciliário · 22 colaboradores",
      result: "Um registo em vez de três",
    },
    relatedSlugs: ["wellness", "offices", "cleaning"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },
];
